import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  PanResponder,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  ActivityIndicator,
  Snackbar,
  useTheme,
} from 'react-native-paper';
import LinearGradient from 'react-native-linear-gradient';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAuth } from '../context/AuthContext';
import DepositService from '../services/depositService';
import ExpenseService from '../services/expenseService';
import JobService from '../services/jobService';
import { Deposit } from '../types/Deposit';
import { Expense } from '../types/Expense';
import { Job } from '../types/Job';
import { ErrorLogger } from '../utils/errorLogger';
import AddDepositBottomSheet from '../components/AddDepositBottomSheet';
import AddExpenseBottomSheet from '../components/AddExpenseBottomSheet';
import ExpenseList from '../components/ExpenseList';
import DepositList from '../components/DepositList';
import ExpenseSummaryCard from '../components/ExpenseSummaryCard';
import DepositSummaryCard from '../components/DepositSummaryCard';

// ─── Types ────────────────────────────────────────────────────────────────────

type Period = 'month' | 'year' | 'all';
type FinanceTab = 'overview' | 'expenses' | 'deposits';

interface BarData {
  label: string;
  income: number;
  deposits: number;
  expenses: number;
}

// ─── Formatters ───────────────────────────────────────────────────────────────

// Full compact amount with currency prefix — used for standalone figures
const fmtShort = (n: number): string => {
  if (n >= 1_000_000) return `UGX ${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `UGX ${(n / 1_000).toFixed(0)}K`;
  return `UGX ${n}`;
};

// Compact amount without prefix — used inside inline labels (e.g. "Cash 300K · Debt 50K")
const compact = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
};

// ─── Date helpers ─────────────────────────────────────────────────────────────

function startOfMonth(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function startOfYear(): Date {
  return new Date(new Date().getFullYear(), 0, 1);
}

// ─── Filter helpers ───────────────────────────────────────────────────────────

function filterDeposits(deposits: Deposit[], period: Period): Deposit[] {
  if (period === 'month') {
    const s = startOfMonth();
    return deposits.filter(d => d.depositDate >= s);
  }
  if (period === 'year') {
    const s = startOfYear();
    return deposits.filter(d => d.depositDate >= s);
  }
  return deposits;
}

function filterExpenses(expenses: Expense[], period: Period): Expense[] {
  if (period === 'month') {
    const s = startOfMonth();
    return expenses.filter(e => e.createdAt >= s);
  }
  if (period === 'year') {
    const s = startOfYear();
    return expenses.filter(e => e.createdAt >= s);
  }
  return expenses;
}

function filterJobs(jobs: Job[], period: Period): Job[] {
  if (period === 'month') {
    const s = startOfMonth();
    return jobs.filter(j => j.createdAt >= s);
  }
  if (period === 'year') {
    const s = startOfYear();
    return jobs.filter(j => j.createdAt >= s);
  }
  return jobs;
}

// ─── Bar chart builders ───────────────────────────────────────────────────────

function buildMonthBars(deposits: Deposit[], expenses: Expense[], jobs: Job[]): BarData[] {
  const bars: BarData[] = ['Wk 1', 'Wk 2', 'Wk 3', 'Wk 4'].map(label => ({
    label, income: 0, deposits: 0, expenses: 0,
  }));
  const som = startOfMonth();
  jobs.forEach(j => {
    if (j.createdAt >= som) {
      bars[Math.min(Math.floor((j.createdAt.getDate() - 1) / 7), 3)].income += j.amount;
    }
  });
  deposits.forEach(d => {
    if (d.depositDate >= som) {
      bars[Math.min(Math.floor((d.depositDate.getDate() - 1) / 7), 3)].deposits += d.amount;
    }
  });
  expenses.forEach(e => {
    if (e.createdAt >= som) {
      bars[Math.min(Math.floor((e.createdAt.getDate() - 1) / 7), 3)].expenses += e.amount;
    }
  });
  return bars;
}

function buildYearBars(deposits: Deposit[], expenses: Expense[], jobs: Job[]): BarData[] {
  const now = new Date();
  const year = now.getFullYear();
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const bars: BarData[] = MONTHS.map(label => ({ label, income: 0, deposits: 0, expenses: 0 }));
  jobs.forEach(j => {
    if (j.createdAt.getFullYear() === year) { bars[j.createdAt.getMonth()].income += j.amount; }
  });
  deposits.forEach(d => {
    if (d.depositDate.getFullYear() === year) { bars[d.depositDate.getMonth()].deposits += d.amount; }
  });
  expenses.forEach(e => {
    if (e.createdAt.getFullYear() === year) { bars[e.createdAt.getMonth()].expenses += e.amount; }
  });
  return bars;
}

function buildAllTimeBars(deposits: Deposit[], expenses: Expense[], jobs: Job[]): BarData[] {
  const allDates = [
    ...jobs.map(j => j.createdAt),
    ...deposits.map(d => d.depositDate),
    ...expenses.map(e => e.createdAt),
  ];
  if (!allDates.length) {
    const y = new Date().getFullYear();
    return [{ label: String(y), income: 0, deposits: 0, expenses: 0 }];
  }
  const minYear = Math.min(...allDates.map(d => d.getFullYear()));
  const maxYear = new Date().getFullYear();
  const bars: BarData[] = Array.from({ length: maxYear - minYear + 1 }, (_, i) => ({
    label: String(minYear + i), income: 0, deposits: 0, expenses: 0,
  }));
  jobs.forEach(j => { bars[j.createdAt.getFullYear() - minYear].income += j.amount; });
  deposits.forEach(d => { bars[d.depositDate.getFullYear() - minYear].deposits += d.amount; });
  expenses.forEach(e => { bars[e.createdAt.getFullYear() - minYear].expenses += e.amount; });
  return bars;
}

// ─── Bar Chart component ──────────────────────────────────────────────────────

const MAX_BAR_H = 72;

const BarChart: React.FC<{
  data: BarData[];
  incomeColor: string;
  primaryColor: string;
  errorColor: string;
  onSurfaceVariant: string;
}> = ({ data, incomeColor, primaryColor, errorColor, onSurfaceVariant }) => {
  const maxVal = Math.max(...data.flatMap(b => [b.income, b.deposits, b.expenses]), 1);
  const h = (val: number) => Math.max((val / maxVal) * MAX_BAR_H, val > 0 ? 4 : 0);
  return (
    <View style={barStyles.root}>
      {data.map(bar => (
        <View key={bar.label} style={barStyles.col}>
          <View style={barStyles.barGroup}>
            <View style={[barStyles.bar, { height: h(bar.income), backgroundColor: incomeColor, marginRight: 1 }]} />
            <View style={[barStyles.bar, { height: h(bar.deposits), backgroundColor: primaryColor, marginRight: 1 }]} />
            <View style={[barStyles.bar, { height: h(bar.expenses), backgroundColor: errorColor }]} />
          </View>
          <Text style={[barStyles.label, { color: onSurfaceVariant }]}>{bar.label}</Text>
        </View>
      ))}
    </View>
  );
};

const barStyles = StyleSheet.create({
  root: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    height: MAX_BAR_H + 24,
  },
  col: {
    flex: 1,
    alignItems: 'center',
  },
  barGroup: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    height: MAX_BAR_H,
  },
  bar: {
    width: 6,
    borderRadius: 3,
  },
  label: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    marginTop: 4,
  },
});

// ─── Main screen ──────────────────────────────────────────────────────────────

const PERIODS: { key: Period; label: string }[] = [
  { key: 'month', label: 'This Month' },
  { key: 'year', label: 'This Year' },
  { key: 'all', label: 'All Time' },
];

const FINANCE_TABS: { key: FinanceTab; label: string; testID: string }[] = [
  { key: 'overview', label: 'Overview', testID: 'tab-overview' },
  { key: 'expenses', label: 'Expenses', testID: 'tab-expenses' },
  { key: 'deposits', label: 'Deposits', testID: 'tab-deposits' },
];

const TAB_KEYS: FinanceTab[] = FINANCE_TABS.map(t => t.key);

const FinancesScreen: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<FinanceTab>('overview');
  const [period, setPeriod] = useState<Period>('month');
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [allDeposits, setAllDeposits] = useState<Deposit[]>([]);
  const [allExpenses, setAllExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [fabOpen, setFabOpen] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showDeleteSnackbar, setShowDeleteSnackbar] = useState(false);
  const [showCreateSnackbar, setShowCreateSnackbar] = useState(false);

  const swipePan = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, gs) =>
        Math.abs(gs.dx) > 12 && Math.abs(gs.dx) > Math.abs(gs.dy),
      onPanResponderRelease: (_, gs) => {
        if (gs.dx < -50) {
          setActiveTab(prev => {
            const i = TAB_KEYS.indexOf(prev);
            return i < TAB_KEYS.length - 1 ? TAB_KEYS[i + 1] : prev;
          });
        } else if (gs.dx > 50) {
          setActiveTab(prev => {
            const i = TAB_KEYS.indexOf(prev);
            return i > 0 ? TAB_KEYS[i - 1] : prev;
          });
        }
      },
    }),
  ).current;

  const loadData = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [jobs, deposits, expenses] = await Promise.all([
        JobService.getAllCompletedJobs(),
        DepositService.getAllDeposits(),
        ExpenseService.getAllExpenses(),
      ]);
      setAllJobs(jobs);
      setAllDeposits(deposits);
      setAllExpenses(expenses);
    } catch (error) {
      ErrorLogger.logError(error as any, 'FinancesScreen.loadData');
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Derived data ────────────────────────────────────────────────────────────

  const jobs = useMemo(() => filterJobs(allJobs, period), [allJobs, period]);
  const deposits = useMemo(() => filterDeposits(allDeposits, period), [allDeposits, period]);
  const expenses = useMemo(() => filterExpenses(allExpenses, period), [allExpenses, period]);

  const cashIncome = useMemo(() => jobs.filter(j => j.paymentType === 'cash').reduce((s, j) => s + j.amount, 0), [jobs]);
  const debtIncome = useMemo(() => jobs.filter(j => j.paymentType === 'debt').reduce((s, j) => s + j.amount, 0), [jobs]);
  const grossIncome = cashIncome + debtIncome;
  const depositTotal = useMemo(() => deposits.reduce((s, d) => s + d.amount, 0), [deposits]);
  const expenseTotal = useMemo(() => expenses.reduce((s, e) => s + e.amount, 0), [expenses]);
  const netBalance = grossIncome + depositTotal - expenseTotal;

  const barData = useMemo(() => {
    if (period === 'month') return buildMonthBars(allDeposits, allExpenses, allJobs);
    if (period === 'year') return buildYearBars(allDeposits, allExpenses, allJobs);
    return buildAllTimeBars(allDeposits, allExpenses, allJobs);
  }, [period, allDeposits, allExpenses, allJobs]);

  const largestDeposit = useMemo(
    () => deposits.reduce<Deposit | null>((max, d) => (!max || d.amount > max.amount ? d : max), null),
    [deposits],
  );
  const largestExpense = useMemo(
    () => expenses.reduce<Expense | null>((max, e) => (!max || e.amount > max.amount ? e : max), null),
    [expenses],
  );

  const topCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    expenses.forEach(e => {
      if (e.category) {
        counts[e.category] = (counts[e.category] ?? 0) + e.amount;
      }
    });
    const entries = Object.entries(counts);
    if (!entries.length) return null;
    return entries.reduce((a, b) => (b[1] > a[1] ? b : a))[0];
  }, [expenses]);

  const categoryBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    expenses.forEach(e => {
      const key = e.category || 'Uncategorised';
      counts[key] = (counts[key] ?? 0) + e.amount;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [expenses]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleItemCreated = () => {
    loadData();
    setShowCreateSnackbar(true);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const chartTitle =
    period === 'month' ? 'Weekly Activity (This Month)'
    : period === 'year' ? `Monthly Activity (${new Date().getFullYear()})`
    : 'Yearly Activity (All Time)';

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.headerTitle, { color: theme.colors.onBackground }]}>
          Finances
        </Text>
      </View>

      {/* Tab switcher */}
      <View style={styles.tabRow}>
        <View style={[styles.tabSwitcher, { backgroundColor: theme.colors.surfaceVariant }]}>
          {FINANCE_TABS.map(t => (
            <TouchableOpacity
              key={t.key}
              testID={t.testID}
              onPress={() => setActiveTab(t.key)}
              style={[
                styles.tabButton,
                activeTab === t.key && { backgroundColor: theme.colors.surface },
              ]}
            >
              <Text
                style={[
                  styles.tabLabel,
                  { color: activeTab === t.key ? theme.colors.onSurface : theme.colors.onSurfaceVariant },
                ]}
              >
                {t.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Swipeable content area */}
      <View style={styles.contentArea} {...swipePan.panHandlers}>

      {/* Overview tab */}
      {activeTab === 'overview' && (
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.scroll}
        >
          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
          ) : (
            <>
              {/* Hero gradient card */}
              <View style={styles.cardWrapper}>
                <LinearGradient
                  colors={['#063669', '#274e82']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.heroCard}
                >
                  <Text style={styles.heroLabel}>GROSS INCOME</Text>
                  <Text style={styles.heroAmount}>{fmtShort(grossIncome)}</Text>
                  <Text style={styles.heroTileSub}>
                    Cash {compact(cashIncome)} · Debt {compact(debtIncome)}
                  </Text>

                  <View style={styles.heroTilesRow}>
                    <View style={styles.heroTile}>
                      <MaterialCommunityIcons name="scale-balance" size={16} color="rgba(255,255,255,0.7)" />
                      <Text style={styles.heroTileLabel}>NET BALANCE</Text>
                      <Text style={styles.heroTileAmount}>{fmtShort(netBalance)}</Text>
                    </View>
                    <View style={[styles.heroTile, styles.heroTileDivider]}>
                      <MaterialCommunityIcons name="bank" size={16} color="rgba(255,255,255,0.7)" />
                      <Text style={styles.heroTileLabel}>DEPOSITS</Text>
                      <Text style={styles.heroTileAmount}>{fmtShort(depositTotal)}</Text>
                    </View>
                    <View style={[styles.heroTile, styles.heroTileDivider]}>
                      <MaterialCommunityIcons name="receipt" size={16} color="rgba(255,255,255,0.7)" />
                      <Text style={styles.heroTileLabel}>EXPENSES</Text>
                      <Text style={styles.heroTileAmount}>{fmtShort(expenseTotal)}</Text>
                    </View>
                  </View>
                </LinearGradient>
              </View>

              {/* Period pills */}
              <View style={styles.periodRow}>
                {PERIODS.map(p => (
                  <TouchableOpacity
                    key={p.key}
                    onPress={() => setPeriod(p.key)}
                    style={[
                      styles.periodPill,
                      period === p.key
                        ? { backgroundColor: theme.colors.primary }
                        : { backgroundColor: theme.colors.surfaceVariant },
                    ]}
                  >
                    <Text
                      style={[
                        styles.periodPillText,
                        { color: period === p.key ? theme.colors.onPrimary : theme.colors.onSurfaceVariant },
                      ]}
                    >
                      {p.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* Bar chart */}
              <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
                <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
                  {chartTitle}
                </Text>
                <View style={styles.legendRow}>
                  <View style={[styles.legendDot, { backgroundColor: '#2e7d32' }]} />
                  <Text style={[styles.legendText, { color: theme.colors.onSurfaceVariant }]}>Income</Text>
                  <View style={[styles.legendDot, { backgroundColor: theme.colors.primary, marginLeft: 12 }]} />
                  <Text style={[styles.legendText, { color: theme.colors.onSurfaceVariant }]}>Deposits</Text>
                  <View style={[styles.legendDot, { backgroundColor: theme.colors.error, marginLeft: 12 }]} />
                  <Text style={[styles.legendText, { color: theme.colors.onSurfaceVariant }]}>Expenses</Text>
                </View>
                <BarChart
                  data={barData}
                  incomeColor="#2e7d32"
                  primaryColor={theme.colors.primary}
                  errorColor={theme.colors.error}
                  onSurfaceVariant={theme.colors.onSurfaceVariant}
                />
              </View>

              {/* Key Insights */}
              <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
                <View style={styles.sectionTitleRow}>
                  <MaterialCommunityIcons name="lightbulb-outline" size={18} color={theme.colors.primary} />
                  <Text style={[styles.sectionTitle, { color: theme.colors.onSurface, marginLeft: 6 }]}>
                    Key Insights
                  </Text>
                </View>

                {largestDeposit ? (
                  <View style={styles.insightRow}>
                    <MaterialCommunityIcons name="bank-outline" size={16} color={theme.colors.primary} />
                    <View style={styles.insightText}>
                      <Text style={[styles.insightLabel, { color: theme.colors.onSurfaceVariant }]}>
                        LARGEST DEPOSIT
                      </Text>
                      <Text style={[styles.insightValue, { color: theme.colors.onSurface }]}>
                        {fmtShort(largestDeposit.amount)}
                        {largestDeposit.description ? ` — ${largestDeposit.description}` : ''}
                      </Text>
                    </View>
                  </View>
                ) : null}

                {largestExpense ? (
                  <View style={styles.insightRow}>
                    <MaterialCommunityIcons name="receipt-outline" size={16} color={theme.colors.error} />
                    <View style={styles.insightText}>
                      <Text style={[styles.insightLabel, { color: theme.colors.onSurfaceVariant }]}>
                        LARGEST EXPENSE
                      </Text>
                      <Text style={[styles.insightValue, { color: theme.colors.onSurface }]}>
                        {fmtShort(largestExpense.amount)}
                        {largestExpense.description ? ` — ${largestExpense.description}` : ''}
                      </Text>
                    </View>
                  </View>
                ) : null}

                {topCategory ? (
                  <View style={styles.insightRow}>
                    <MaterialCommunityIcons name="tag-outline" size={16} color={theme.colors.secondary} />
                    <View style={styles.insightText}>
                      <Text style={[styles.insightLabel, { color: theme.colors.onSurfaceVariant }]}>
                        TOP EXPENSE CATEGORY
                      </Text>
                      <Text style={[styles.insightValue, { color: theme.colors.onSurface }]}>
                        {topCategory}
                      </Text>
                    </View>
                  </View>
                ) : null}

                {!largestDeposit && !largestExpense && (
                  <Text style={[styles.emptyInsight, { color: theme.colors.onSurfaceVariant }]}>
                    No activity for this period yet.
                  </Text>
                )}
              </View>

              {/* Expenses by Category */}
              {categoryBreakdown.length > 0 && (
                <View style={[styles.section, { backgroundColor: theme.colors.surface }]}>
                  <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
                    Expenses by Category
                  </Text>
                  {categoryBreakdown.map(([cat, total]) => {
                    const pct = expenseTotal > 0 ? (total / expenseTotal) * 100 : 0;
                    return (
                      <View key={cat} style={styles.breakdownRow}>
                        <Text
                          style={[styles.breakdownLabel, { color: theme.colors.onSurface }]}
                          numberOfLines={1}
                        >
                          {cat}
                        </Text>
                        <View style={styles.breakdownBarTrack}>
                          <View
                            style={[
                              styles.breakdownBarFill,
                              { width: `${pct}%` as any, backgroundColor: theme.colors.primary },
                            ]}
                          />
                        </View>
                        <Text style={[styles.breakdownAmount, { color: theme.colors.onSurfaceVariant }]}>
                          {fmtShort(total)}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              )}

              <View style={{ height: 80 }} />
            </>
          )}
        </ScrollView>
      )}

      {/* Expenses tab */}
      {activeTab === 'expenses' && (
        <>
          <ExpenseSummaryCard />
          <ExpenseList onDeleteSuccess={() => setShowDeleteSnackbar(true)} />
        </>
      )}

      {/* Deposits tab */}
      {activeTab === 'deposits' && (
        <>
          <DepositSummaryCard />
          <DepositList onDeleteSuccess={() => setShowDeleteSnackbar(true)} />
        </>
      )}

      </View>{/* end swipeable content area */}

      {/* FAB — speed-dial on overview, single action on expenses/deposits */}
      <View style={styles.fabContainer} pointerEvents="box-none">
        {activeTab === 'overview' && fabOpen && (
          <>
            <TouchableOpacity
              style={styles.actionRow}
              onPress={() => { setFabOpen(false); setShowDepositModal(true); }}
              activeOpacity={0.8}
            >
              <Text style={[styles.actionLabel, { backgroundColor: theme.colors.surface, color: theme.colors.onSurface }]}>
                Add Deposit
              </Text>
              <View style={[styles.actionFab, { backgroundColor: theme.colors.primary }]}>
                <MaterialCommunityIcons name="bank-plus" size={20} color={theme.colors.onPrimary} />
              </View>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionRow}
              onPress={() => { setFabOpen(false); setShowExpenseModal(true); }}
              activeOpacity={0.8}
            >
              <Text style={[styles.actionLabel, { backgroundColor: theme.colors.surface, color: theme.colors.onSurface }]}>
                Add Expense
              </Text>
              <View style={[styles.actionFab, { backgroundColor: theme.colors.primary }]}>
                <MaterialCommunityIcons name="receipt" size={20} color={theme.colors.onPrimary} />
              </View>
            </TouchableOpacity>
          </>
        )}

        <TouchableOpacity
          style={[styles.mainFab, { backgroundColor: theme.colors.primary }]}
          onPress={() => {
            if (activeTab === 'expenses') { setShowExpenseModal(true); return; }
            if (activeTab === 'deposits') { setShowDepositModal(true); return; }
            setFabOpen(o => !o);
          }}
          activeOpacity={0.8}
        >
          <MaterialCommunityIcons
            name={activeTab === 'overview' && fabOpen ? 'close' : 'plus'}
            size={24}
            color={theme.colors.onPrimary}
          />
        </TouchableOpacity>
      </View>

      <AddDepositBottomSheet
        visible={showDepositModal}
        onDepositCreated={handleItemCreated}
        onClose={() => setShowDepositModal(false)}
        onSuccess={() => {}}
      />
      <AddExpenseBottomSheet
        visible={showExpenseModal}
        onExpenseCreated={handleItemCreated}
        onClose={() => setShowExpenseModal(false)}
        onSuccess={() => {}}
      />

      <Snackbar
        visible={showDeleteSnackbar}
        onDismiss={() => setShowDeleteSnackbar(false)}
        duration={3000}
        action={{ label: 'OK', onPress: () => setShowDeleteSnackbar(false) }}
      >
        Deleted successfully!
      </Snackbar>
      <Snackbar
        visible={showCreateSnackbar}
        onDismiss={() => setShowCreateSnackbar(false)}
        duration={3000}
        action={{ label: 'OK', onPress: () => setShowCreateSnackbar(false) }}
      >
        Added successfully!
      </Snackbar>
    </SafeAreaView>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1 },
  contentArea: { flex: 1 },
  scroll: { paddingBottom: 16 },
  header: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 8,
  },
  headerTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 22,
  },
  tabRow: {
    paddingHorizontal: 20,
    paddingBottom: 12,
  },
  tabSwitcher: {
    flexDirection: 'row',
    borderRadius: 8,
    padding: 3,
  },
  tabButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  tabLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
  },
  periodRow: {
    flexDirection: 'row',
    gap: 4,
    paddingHorizontal: 12,
    marginBottom: 16,
  },
  periodPill: {
    flex: 1,
    paddingHorizontal: 4,
    paddingVertical: 5,
    borderRadius: 9999,
    alignItems: 'center',
  },
  periodPillText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    lineHeight: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 48,
  },
  // Hero card
  cardWrapper: {
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  heroCard: {
    borderRadius: 6,
    padding: 20,
  },
  heroLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    letterSpacing: 1,
    color: 'rgba(255,255,255,0.6)',
    marginBottom: 4,
  },
  heroAmount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 32,
    color: '#ffffff',
    marginBottom: 20,
  },
  heroTilesRow: {
    flexDirection: 'row',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: 'rgba(255,255,255,0.2)',
    paddingTop: 16,
  },
  heroTile: {
    flex: 1,
    gap: 4,
  },
  heroTileDivider: {
    borderLeftWidth: StyleSheet.hairlineWidth,
    borderLeftColor: 'rgba(255,255,255,0.2)',
    paddingLeft: 16,
    marginLeft: 16,
  },
  heroTileLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 10,
    letterSpacing: 0.8,
    color: 'rgba(255,255,255,0.55)',
    marginTop: 4,
  },
  heroTileAmount: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 18,
    color: '#ffffff',
  },
  heroTileSub: {
    fontFamily: 'Inter-Regular',
    fontSize: 10,
    color: 'rgba(255,255,255,0.5)',
    marginTop: 2,
    marginBottom: 16,
  },
  // Sections
  section: {
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: 6,
    padding: 16,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontFamily: 'Manrope-SemiBold',
    fontSize: 15,
    marginBottom: 12,
  },
  // Legend
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 4,
  },
  legendText: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
  },
  // Insights
  insightRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
    gap: 10,
  },
  insightText: {
    flex: 1,
  },
  insightLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 10,
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  insightValue: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
  },
  emptyInsight: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 8,
  },
  // Breakdown
  breakdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 8,
  },
  breakdownLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    width: 90,
  },
  breakdownBarTrack: {
    flex: 1,
    height: 6,
    backgroundColor: 'rgba(6, 54, 105, 0.1)',
    borderRadius: 3,
    overflow: 'hidden',
  },
  breakdownBarFill: {
    height: 6,
    borderRadius: 3,
  },
  breakdownAmount: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    width: 60,
    textAlign: 'right',
  },
  // Speed-dial FAB
  fabContainer: {
    position: 'absolute',
    bottom: 16,
    right: 16,
    alignItems: 'flex-end',
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  actionLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 4,
    marginRight: 10,
    elevation: 2,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
  },
  actionFab: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 3,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
  },
  mainFab: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
    shadowColor: '#111c2d',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    marginTop: 4,
  },
});

export default FinancesScreen;
